import type * as maplibregl from "maplibre-gl";
import { POI_COLOR, POI_ICON_SVG_PATHS, poiGlyphSignature } from "./poiIcons";

const CLUSTER_IMAGE_PREFIX = "poi-cluster-chip-";
const ICON_SIZE = 14;
const ICON_GAP = 4;
const CHIP_PADDING = 4;
const CHIP_BORDER = 1.5;
const CHIP_MAX_WIDTH = 76;
const MAX_COLUMNS = 3;
const SHADOW_MARGIN = 6;
const PIXEL_RATIO = 2;

export interface PoiClusterGlyph {
  kind: string;
  kinds: string[];
  property: string;
}

export const buildPoiClusterGlyphs = (excludedKinds: readonly string[]): PoiClusterGlyph[] => {
  const excluded = new Set(excludedKinds);
  const bySignature = new Map<string, PoiClusterGlyph>();
  for (const kind of Object.keys(POI_ICON_SVG_PATHS)) {
    if (excluded.has(kind)) continue;
    const signature = poiGlyphSignature(kind);
    const existing = bySignature.get(signature);
    if (existing) {
      existing.kinds.push(kind);
      continue;
    }
    bySignature.set(signature, {
      kind,
      kinds: [kind],
      property: `poi_glyph_${bySignature.size}`,
    });
  }
  return [...bySignature.values()];
};

export const buildPoiClusterProperties = (glyphs: PoiClusterGlyph[]) =>
  Object.fromEntries(
    glyphs.map(({ kinds, property }) => [
      property,
      ["+", ["match", ["get", "kind"], kinds, 1, 0]],
    ]),
  );

export const buildPoiClusterImageExpression = (glyphs: PoiClusterGlyph[]): unknown[] => [
  "concat",
  CLUSTER_IMAGE_PREFIX,
  ...glyphs.map(({ property }) => ["case", [">", ["get", property], 0], "1", "0"]),
];

const addRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  const right = x + width;
  const bottom = y + height;
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(right - radius, y);
  ctx.quadraticCurveTo(right, y, right, y + radius);
  ctx.lineTo(right, bottom - radius);
  ctx.quadraticCurveTo(right, bottom, right - radius, bottom);
  ctx.lineTo(x + radius, bottom);
  ctx.quadraticCurveTo(x, bottom, x, bottom - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const drawGlyph = (ctx: CanvasRenderingContext2D, kind: string, x: number, y: number) => {
  const spec = POI_ICON_SVG_PATHS[kind];
  if (!spec) return;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(ICON_SIZE / spec.viewBox, ICON_SIZE / spec.viewBox);
  if (spec.originX || spec.originY) ctx.translate(-(spec.originX ?? 0), -(spec.originY ?? 0));
  if (spec.strokeWidth) {
    ctx.strokeStyle = POI_COLOR;
    ctx.lineWidth = spec.strokeWidth;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    for (const path of spec.paths) ctx.stroke(new Path2D(path));
  } else {
    ctx.fillStyle = POI_COLOR;
    for (const path of spec.paths) ctx.fill(new Path2D(path), spec.fillRule ?? "nonzero");
  }
  ctx.restore();
};

const buildPoiClusterImage = (kinds: string[]): ImageData | null => {
  if (kinds.length === 0) return null;
  const columns = Math.min(MAX_COLUMNS, kinds.length);
  const rows = Math.ceil(kinds.length / MAX_COLUMNS);
  const naturalWidth = columns * ICON_SIZE + (columns - 1) * ICON_GAP + CHIP_PADDING * 2 + CHIP_BORDER * 2;
  const chipWidth = kinds.length > MAX_COLUMNS ? CHIP_MAX_WIDTH : naturalWidth;
  const chipHeight = rows * ICON_SIZE + (rows - 1) * ICON_GAP + CHIP_PADDING * 2 + CHIP_BORDER * 2;
  const width = Math.ceil((chipWidth + SHADOW_MARGIN * 2) * PIXEL_RATIO);
  const height = Math.ceil((chipHeight + SHADOW_MARGIN * 2) * PIXEL_RATIO);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.scale(PIXEL_RATIO, PIXEL_RATIO);

  const x = SHADOW_MARGIN;
  const y = SHADOW_MARGIN;
  const radius = chipHeight / 2;
  addRoundedRect(ctx, x, y, chipWidth, chipHeight, radius);
  ctx.fillStyle = "#ffffff";
  ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
  ctx.shadowBlur = 5;
  ctx.shadowOffsetY = 1;
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.lineWidth = CHIP_BORDER;
  ctx.strokeStyle = POI_COLOR;
  ctx.stroke();

  for (let row = 0; row < rows; row += 1) {
    const rowKinds = kinds.slice(row * MAX_COLUMNS, (row + 1) * MAX_COLUMNS);
    const rowWidth = rowKinds.length * ICON_SIZE + (rowKinds.length - 1) * ICON_GAP;
    const rowX = x + (chipWidth - rowWidth) / 2;
    const rowY = y + CHIP_BORDER + CHIP_PADDING + row * (ICON_SIZE + ICON_GAP);
    rowKinds.forEach((kind, column) => {
      drawGlyph(ctx, kind, rowX + column * (ICON_SIZE + ICON_GAP), rowY);
    });
  }
  return ctx.getImageData(0, 0, width, height);
};

export const registerPoiClusterImageResolver = (map: maplibregl.Map, glyphs: PoiClusterGlyph[]) => {
  map.on("styleimagemissing", ({ id }) => {
    if (!id.startsWith(CLUSTER_IMAGE_PREFIX) || map.hasImage(id)) return;
    const mask = id.slice(CLUSTER_IMAGE_PREFIX.length);
    if (mask.length !== glyphs.length || /[^01]/.test(mask)) return;
    const kinds = glyphs.filter((_, index) => mask[index] === "1").map(({ kind }) => kind);
    const image = buildPoiClusterImage(kinds);
    if (image) map.addImage(id, image, { pixelRatio: PIXEL_RATIO });
  });
};
